import { DSFlex, DSSpacer, DSText } from '@zoe/ds-web'
import React from 'react'
import { ResponsiveImageType } from 'react-datocms/image'
import styled from 'styled-components'

import { FragmentComponent } from '@/types/graphql'

import { DSGridBlock } from '@/components/ds/Grid/Grid'

import { responsiveImageBlock } from '@/lib/api/graphql/utils/commonBlocks'

import { processTypographyMarkdown } from '../DesignSystem/Typography'

export type CardsBlockCard = {
  image: {
    responsiveImage: ResponsiveImageType
  }
  title: string
  description: string
  isImageCircular?: boolean
}

export type CardsSectionRecord = {
  __typename: 'CardsSectionRecord'
  id: string
  cards: CardsBlockCard[]
  dataTestId?: string
  eyebrow?: string
  title?: string
  description?: string
}

const fragment = `
  fragment CardsBlockFragment on CardsSectionRecord {
    __typename
    id
    dataTestId
    eyebrow
    title
    description
    cards {
      image {
        ${responsiveImageBlock({ width: 640, height: 640 })}
      }
      title
      description
      isImageCircular
    }
  }
`

const StyledContentContainer = styled.div`
  @media (min-width: 848px) {
    max-width: 848px;
    margin: 0 auto;
  }
`

const StyledCardsContainer = styled.div`
  @media (min-width: 1074px) {
    max-width: 1074px;
    margin: 0 auto;
  }
`

export const CardsBlock: FragmentComponent<{}, CardsSectionRecord> = ({ record }) => {
  return (
    <>
      <DSGridBlock
        dataTestId={record?.dataTestId}
        padded
        breakConfig={{
          s: { colSpan: 2 },
          m: { colSpan: 6 },
          l: { colSpan: 8, offset: 2 },
          xl: { colSpan: 8, offset: 2 },
        }}
      >
        <StyledContentContainer>
          <DSSpacer direction="vertical" size={72} />
          {record.eyebrow && (
            <DSText as="h5" variant="fluid-heading-500alt" weight="semiBold" align="center">
              {record.eyebrow}
            </DSText>
          )}
          <DSSpacer direction="vertical" size={16} breakAt={{ m: 8 }} />
          {record.title && (
            <DSText as="h1" variant="fluid-heading-900alt" weight="semiBold" align="center">
              {processTypographyMarkdown({ content: record.title, variant: 'fluid-heading-900alt' })}
            </DSText>
          )}
          <DSSpacer direction="vertical" size={24} />
          {record.description && (
            <DSText as="p" variant="fluid-paragraph-400" weight="light" align="center">
              {processTypographyMarkdown({ content: record.description, variant: 'fluid-paragraph-400' })}
            </DSText>
          )}
        </StyledContentContainer>
      </DSGridBlock>

      <DSSpacer direction="vertical" size={48} />

      <DSGridBlock
        dataTestId={record?.dataTestId}
        padded
        breakConfig={{
          s: { colSpan: 2 },
          m: { colSpan: 6 },
          l: { colSpan: 10, offset: 1 },
          xl: { colSpan: 10, offset: 1 },
        }}
      >
        <StyledCardsContainer>
          <DSFlex
            breakConfig={{
              maxViewportDirection: 'row',
              breakAt: {
                breakPoint: 'm',
                direction: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 48,
              },
            }}
            justifyContent="center"
            wrap="wrap"
            gap={40}
          >
            {record.cards?.map((card, idx) => (
              <div style={{ maxWidth: '330px' }} key={`card-${idx}`}>
                <DSFlex key={idx} direction="column" alignItems="center">
                  <img
                    src={card.image?.responsiveImage?.src || ''}
                    alt={card.image?.responsiveImage?.alt || ''}
                    style={{ width: '100%', height: 'auto', borderRadius: card.isImageCircular ? '50%' : '16px' }}
                  />
                  <DSSpacer direction="vertical" size={24} breakAt={{ m: 32 }} />
                  <DSText as="h5" variant="fluid-heading-500alt" weight="semiBold" align="center">
                    {processTypographyMarkdown({ content: card.title, variant: 'fluid-heading-500alt' })}
                  </DSText>
                  <DSSpacer direction="vertical" size={8} />
                  <DSText as="p" variant="fluid-paragraph-300" weight="light" align="center">
                    {processTypographyMarkdown({ content: card.description, variant: 'fluid-paragraph-300' })}
                  </DSText>
                </DSFlex>
              </div>
            ))}
          </DSFlex>
          <DSSpacer direction="vertical" size={48} />
        </StyledCardsContainer>
      </DSGridBlock>
    </>
  )
}

CardsBlock.fragment = fragment
CardsBlock.recordName = 'CardsSectionRecord'
